import { defaultLanguages, type ZudokuConfig } from "zudoku";
import { internalAllowedTenants } from "./src/utils/allowed-tenants";
import { createApiIdentityPlugin } from "zudoku/plugins";
import createTenantNavigationPlugin from "./zudoku/plugins/tenant-navigation";
import RequireTenant from "./src/components/RequireTenant";
import RequireAuth from "./src/components/RequireAuth";

const config: ZudokuConfig = {
  plugins: [
    createApiIdentityPlugin({
      getIdentities: async (context) => [
        {
          id: "tech231-platform",
          label: "Tech231 Platform",
          authorizeRequest: async (request) => {
            // We get the access token from the
            // authentication provider (Auth0) and add it to the request headers
            if (!context.authentication) {
              return request;
            }
            const token = await context.authentication.signRequest(request);
            if (token) {
              request.headers.set("Authorization", `Bearer ${token}`);
            }
            return request;
          },
        },
      ],
    }),
    createTenantNavigationPlugin({
      tenantClaim: "urn:zitadel:iam:user:resourceowner:id",
    }),
  ],
  port: process.env.PORT ? parseInt(process.env.PORT) : 3000,
  syntaxHighlighting: {
    themes: {
      light: "github-dark",
      dark: "github-dark-dimmed",
    },
    languages: [...defaultLanguages, "hcl"],
  },
  site: {
    logo: {
      src: { light: "/tech231-logo.svg", dark: "/tech231-logo.svg" },
      alt: "Tech231 Platform",
      width: "130px",
    },
  },
  authentication: {
    type: "openid",
    clientId: "341173767826899583",
    scopes: [
      "email",
      "openid",
      "offline_access",
      "profile",
      "roles",
      "urn:zitadel:iam:org:project:id:321914919689650215:aud",
      "urn:zitadel:iam:user:resourceowner",
    ],
    issuer: "https://auth.tech231apps.net",
    redirectToAfterSignUp: "/",
    redirectToAfterSignIn: "/",
    redirectToAfterSignOut: "/",
  },
  navigation: [
    {
      type: "category",
      label: "Customer Documentation",
      items: [
        {
          type: "category",
          label: "Start Here",
          icon: "sparkles",
          items: [
            {
              type: "doc",
              file: "introduction",
              path: "/",
            },
            "/getting-started/customer-quickstart",
            {
              type: "link",
              icon: "folder-cog",
              badge: {
                label: "API",
                color: "green",
              },
              label: "API Reference",
              to: "/api",
            },
          ],
        },
        {
          type: "category",
          label: "Messaging",
          icon: "message-square",
          items: [
            {
              type: "category",
              label: "Sender IDs",
              icon: "id-card",
              items: [
                "/senderid/customer-management",
                "/senderid/acquisition-flow",
              ],
            },
            {
              type: "category",
              label: "SMS",
              icon: "server",
              items: [
                "/integration-guides/sms-sending-flows",
                "/integration-guides/sms-analytics",
                "/integration-guides/sms-status-webhooks",
              ],
            },
            {
              type: "category",
              label: "OTP",
              icon: "key-round",
              items: [
                "/integration-guides/otp-service",
                "/integration-guides/otp-examples",
              ],
            },
          ],
        },
        {
          type: "category",
          label: "USSD Applications",
          icon: "phone",
          items: [
            {
              type: "category",
              label: "Basics",
              items: [
                "/ussd/getting-started",
                "/ussd/dsl-reference",
                "/ussd/template-variables",
                "/ussd/navigation",
                "/ussd/data-driven-menus",
              ],
            },
            {
              type: "category",
              label: "Build & Test",
              items: [
                "/integration-guides/ussd-dsl-guide",
                "/integration-guides/ussd-dsl-actions",
                "/integration-guides/ussd-dsl-action-chains",
                "/integration-guides/ussd-dsl-banking-case-study",
                "/integration-guides/ussd-testing-payloads",
              ],
            },
          ],
        },
        {
          type: "category",
          label: "Security",
          icon: "shield",
          items: [
            "/security/authentication-setup",
            "/security/security-policy",
            "/security/otp-security",
          ],
        },
      ],
    },
    {
      type: "category",
      label: "Developer Documentation",
      // restrict this category to internal tenants; single source of truth
      // @ts-ignore - custom property used for tenant-based nav filtering plugin
      allowedTenants: internalAllowedTenants,
      display: "auth",
      items: [
        {
          type: "category",
          label: "Platform Setup",
          icon: "code",
          items: [
            "/getting-started/README",
            "/getting-started/dotnet-sdk-install",
          ],
        },
        {
          type: "category",
          label: "Architecture",
          icon: "building",
          items: [
            {
              type: "category",
              label: "Platform & Messaging",
              items: [
                "/architecture/portal-runtime-architecture",
                "/architecture/orleans-sms-integration",
                "/architecture/sms-message-history-and-smpp-ingress",
                "/architecture/sms-analytics",
              ],
            },
            {
              type: "category",
              label: "Sender ID & USSD",
              items: [
                "/architecture/senderid-architecture-overview",
                "/architecture/ussd-gateway-flow",
                "/architecture/ussd-application-management",
                "/architecture/ussd-networking-proxy",
              ],
            },
          ],
        },
        {
          type: "category",
          label: "Integrations & Identity",
          icon: "plug",
          items: [
            "/integration-guides/inbound-sms-webhook",
            "/integration-guides/inbound-sms-routing",
            "/integration-guides/zitadel-tenant-sync",
          ],
        },
        {
          type: "category",
          label: "Finance & Billing",
          icon: "credit-card",
          items: [
            {
              type: "category",
              label: "Concepts",
              items: [
                "/finance/overview",
                "/finance/status-legend",
                "/finance/billing-catalog",
                "/finance/pricing-models",
              ],
            },
            {
              type: "category",
              label: "Customer Accounts",
              items: [
                "/finance/invoices-payments",
                "/finance/wallets",
              ],
            },
            {
              type: "category",
              label: "Implementation",
              items: [
                "/finance/rbac-authorization",
                "/finance/api-reference",
                "/finance/billing-events-pipeline",
                "/finance/billing-replay-and-settlement-internals",
                "/finance/operational-tools",
                "/finance/integration-guide",
              ],
            },
          ],
        },
        {
          type: "category",
          label: "Internal Operations",
          icon: "settings",
          items: [
            "/security/platform-rbac-internal",
            "/senderid/operations-guide",
            "/senderid/state-transitions",
            "/internals/smsc-connection-manager-usage",
            "/internals/sms-queued-state-incident-2026-07-14",
            "/internals/health-probe-telemetry-filtering",
          ],
        },
        {
          type: "category",
          label: "USSD Internals",
          icon: "phone",
          items: [
            "/internals/ussd-request-pipeline",
            "/internals/ussd-switch-routing-and-remote-auth",
            "/internals/ussd-dsl-redirect-sequences",
            "/internals/cellcube-xmlhttp-protocol-integration",
            "/internals/ussd-integration-testing",
            "/internals/ussd-dsl-extended-actions-adr",
            "/internals/ussd-request-examples",
          ],
        },
        {
          type: "category",
          label: "Migration",
          icon: "refresh-cw",
          items: ["/migration/analytics-v2-to-timescale"],
        },
        {
          type: "category",
          label: "Useful Links",
          collapsible: false,
          icon: "link",
          items: [
            {
              type: "link",
              icon: "book",
              label: "Zudoku Docs",
              to: "https://zudoku.dev/docs/",
            },
          ],
        },
      ],
    },
    {
      type: "category",
      label: "Releases",
      icon: "rocket",
      items: ["/releases/index", "/releases/v2026.1.89"],
    },
    {
      type: "link",
      to: "/api-catalogue",
      label: "API Catalogue",
    },
  ],
  mdx: {
    components: {
      RequireAuth,
      RequireTenant,
    },
  },
  redirects: [{ from: "/introduction", to: "/" }],
  apis: [
    {
      type: "url",

      input: "https://staging-sms-api.tech231apps.net/openapi/v2.json",
      path: "/api-catalogue/sms-api",
    },
    {
      type: "url",
      input: "https://staging-ussd-api.tech231apps.net/openapi/v1.json",
      path: "/api-catalogue/ussd-api",
    },
  ],
  docs: {
    files: ["/pages/**/*.{md,mdx}"],
    defaultOptions: {
      toc: true, // Show table of contents
      showLastModified: true, // Show last modified date
    },
  },
};

export default config;
